// Ekran 07 — "Menüye kalem ekle" drawer'ı.
// ref_files/btys-erp-07-besleme.html openDrawer/keList/kalemEkle davranışı:
// iki filtre (firma · ürün) → eşleşen havuzlar radio listesi → miktar.
//
// İki kural burada görünür:
//   K26 — menüye giren şey stok kartı değil, FİRMA×ÜRÜN eşleşmesidir.
//   K27 — filtreler bir firma+ürün seçti ama havuz yoksa "girişsiz eşleşme"
//         amber seçenek olarak çıkar; eklenirse plan kalemi olur, stok düşmez.

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde } from '../../components/Drawer'
import { tr, sayiyaCevir } from '../../lib/trFormat'
import { cariler as cariApi, havuzlar, stokKartlari, type Havuz } from '../masterdata/api'

const KAYNAK: Record<string, [string, string]> = {
  analiz: ['Analiz', 'src-analiz'],
  tesis: ['Tesis', 'src-tesis'],
  lit: ['Literatür', 'src-lit'],
}

/**
 * Havuzun etkin KM'sinin nereden geldiğini bir cümlede anlatır (K13) —
 * referans 07'nin `etkin().not` kalıpları.
 *
 * "Tesis" kalıbı hangi sayının hangisinin yerine geçtiğini yazmalı: rozet tek
 * başına tesis değerinin literatürden ne kadar saptığını göstermiyor.
 * Literatür KM'si havuz DTO'sunda yok, ürün filtresi için zaten çekilen
 * hammadde kartından geliyor (aşağıdaki `kartlar` sözlüğü).
 *
 * Analiz kalıbının referanstaki "son NUM-0187 · 14.08" parçası hiçbir istemci
 * verisinde yok (havuz DTO'su son numuneyi taşımıyor); uydurma bir numune
 * yazmaktansa cümle o parçasız kalıyor (K32).
 */
function kaynakNotu(h: Havuz, literaturKm: number | null | undefined): string {
  if (h.analizSayisi > 0) return `${h.analizSayisi} analiz · bu eşleşmenin ölçümü`
  if (h.etkinKm.kaynak === 'tesis') {
    return literaturKm == null
      ? `tesis değeri ${tr(h.etkinKm.deger, 1)}`
      : `tesis değeri ${tr(h.etkinKm.deger, 1)} (literatür ${tr(literaturKm, 1)})`
  }
  return 'bu eşleşmede analiz yok — kart/literatür'
}

type Props = {
  ekle: (havuzId: string, miktarT: number, planMi: boolean) => void
  kapat: () => void
}

export default function KalemEkleDrawer({ ekle, kapat }: Props) {
  const [cariId, setCariId] = useState('')
  const [stokId, setStokId] = useState('')
  const [secim, setSecim] = useState<string | null>(null)
  const [miktar, setMiktar] = useState('5')
  const [planKuruluyor, setPlanKuruluyor] = useState(false)

  const { data: cariListesi = [] } = useQuery({
    queryKey: ['cariler', 'atik'],
    queryFn: () => cariApi.liste(undefined, 'atik'),
  })

  const { data: kartListesi = [] } = useQuery({
    queryKey: ['stok-kartlari', 'hammadde'],
    queryFn: () => stokKartlari.liste('hammadde'),
  })

  const { data: havuzListesi = [], isLoading } = useQuery({
    queryKey: ['havuzlar', cariId, stokId],
    queryFn: () => havuzlar.liste({ cariId: cariId || undefined, stokId: stokId || undefined }),
  })

  // Havuz DTO'su kartın hijyen bayrağını (K19) ve literatür KM'sini taşımıyor;
  // ikisi de ürün filtresi için zaten çekilen kart listesinde var — kimlikten
  // bakılıyor, ek istek açılmıyor.
  const kartlar = useMemo(() => new Map(kartListesi.map((k) => [k.id, k])), [kartListesi])

  // Bakiyesi büyük olan üstte — referansın sıralaması.
  const liste = useMemo(
    () => [...havuzListesi].sort((a, b) => b.bakiye - a.bakiye),
    [havuzListesi],
  )

  // K27: iki filtre de seçili ve eşleşen havuz yok → girişsiz eşleşme.
  const girissizEslesme = Boolean(cariId) && Boolean(stokId) && !isLoading && liste.length === 0

  // Tek sonuç kendiliğinden seçili (§8-07 kabul kriteri). Seçim listede
  // kalmadıysa düşürülür — filtre değişince ölü seçim taşınmasın.
  const etkinSecim = useMemo(() => {
    if (girissizEslesme) return 'PLAN'
    if (liste.length === 1) return liste[0].id
    if (secim && liste.some((h) => h.id === secim)) return secim
    return null
  }, [girissizEslesme, liste, secim])

  const secilenKart = kartListesi.find((k) => k.id === stokId)
  const secilenCari = cariListesi.find((c) => c.id === cariId)

  async function gonder() {
    const t = sayiyaCevir(miktar)
    if (!Number.isFinite(t) || t <= 0) return

    if (etkinSecim === 'PLAN') {
      if (!cariId || !stokId) return
      setPlanKuruluyor(true)
      try {
        // Havuz satırı olmadan menü kalemi bağlanamaz; plan havuzu açılıyor.
        // İlk LOT onayında aynı satır gerçek havuza döner (K27).
        const havuz = await havuzlar.plan(cariId, stokId)
        ekle(havuz.id, t, true)
      } finally {
        setPlanKuruluyor(false)
      }
      return
    }

    const secili = liste.find((h) => h.id === etkinSecim)
    if (!secili) return
    ekle(secili.id, t, secili.planMi)
  }

  const butonMetni = etkinSecim === 'PLAN' ? 'Plan kalemi olarak ekle' : 'Menüye ekle'

  return (
    <>
      <DrawerGovde>
        <div className="fgrid" style={{ marginBottom: 8 }}>
          <div className="fld">
            <label>Firma (atık sağlayıcı) — filtre</label>
            <select
              className="in"
              value={cariId}
              onChange={(e) => { setCariId(e.target.value); setSecim(null) }}
            >
              <option value="">Tümü</option>
              {cariListesi.map((c) => (
                <option key={c.id} value={c.id}>{c.unvan}</option>
              ))}
            </select>
          </div>
          <div className="fld">
            <label>Ürün (stok kartı) — filtre</label>
            <select
              className="in"
              value={stokId}
              onChange={(e) => { setStokId(e.target.value); setSecim(null) }}
            >
              <option value="">Tümü</option>
              {kartListesi.map((k) => (
                <option key={k.id} value={k.id}>{k.ad}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="insp-sec-t" style={{ marginBottom: 5 }}>
          Olası stoklar — {liste.length} havuz{girissizEslesme ? ' + 1 girişsiz eşleşme' : ''}
        </div>

        <div style={{ maxHeight: 296, overflow: 'auto' }}>
          {liste.map((h) => {
            const [etiket, sinif] = KAYNAK[h.etkinKm.kaynak ?? 'lit'] ?? KAYNAK.lit
            const kart = kartlar.get(h.stokKartiId)
            return (
              <label className="lib-i" key={h.id}>
                <input
                  type="radio"
                  name="kp"
                  checked={etkinSecim === h.id}
                  onChange={() => setSecim(h.id)}
                />
                <div>
                  <div className="lib-n">
                    {h.stokAd} <span className={`src-b ${sinif}`}>{etiket}</span>
                    {kart?.hij && <>{' '}<span className="src-b src-tesis">Hijyen</span></>}
                    {h.planMi && <>{' '}<span className="src-b src-tesis">Girişsiz</span></>}
                  </div>
                  <div className="lib-m">
                    {h.cariUnvan} · {kaynakNotu(h, kart?.km)} · KM {tr(h.etkinKm.deger, 2)}
                  </div>
                </div>
                <span className="lib-v">{h.planMi ? '—' : `${tr(h.bakiye, 1)} t`}</span>
              </label>
            )
          })}

          {girissizEslesme && (
            <label
              className="lib-i"
              style={{ background: 'var(--amber-bg)', borderColor: 'var(--amber-bd)' }}
            >
              <input type="radio" name="kp" checked readOnly />
              <div>
                <div className="lib-n" style={{ color: 'var(--amber)' }}>
                  ⚠ {secilenCari?.unvan} + {secilenKart?.ad} — hiç mal kabulü yok
                </div>
                <div className="lib-m">
                  Kart/literatür değerleriyle <b>plan kalemi</b> eklenir — stok düşümü
                  yapılmaz, ilk kabulde havuz oluşur.
                </div>
              </div>
              <span className="lib-v">0 t</span>
            </label>
          )}

          {!girissizEslesme && liste.length === 0 && !isLoading && (
            <div className="insp-empty" style={{ padding: '22px 10px' }}>
              Filtreye uyan stok havuzu yok — firma/ürün seçimini genişletin.
            </div>
          )}
        </div>

        <div className="fld" style={{ marginTop: 9 }}>
          <label>Miktar (t)</label>
          <input
            className="in mono"
            value={miktar}
            onChange={(e) => setMiktar(e.target.value)}
          />
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={kapat}>Vazgeç</button>
        <button
          className="btn btn-pri"
          type="button"
          disabled={!etkinSecim || planKuruluyor}
          style={{ opacity: etkinSecim && !planKuruluyor ? 1 : 0.5 }}
          onClick={gonder}
        >
          {planKuruluyor ? 'Havuz açılıyor…' : butonMetni}
        </button>
      </DrawerAltlik>
    </>
  )
}

export { KAYNAK }
